/* globals showModalConfirm */
import React, { useContext, useState } from "react";
import {
  parseFirewallList,
  composeApplyCommand,
  areChangesColliding,
} from "./api";
import { parseManagementInterface } from "views/Config/Interfaces/Management/api";
import SettingsView from "common/SettingsView";
import useProfile from "common/hooks/useProfile";
import Ranges from "./Ranges";
import ActionsContext from "common/ActionsContext";
import help from "help/viewManagementFirewallConf.md";
import { atBqnTarget, parseClusterResponse } from "common/api";
import BqnContext from "common/BqnContext";
import Loading from "common/Loading";

const doesNothing = () => {};

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const getFirewallList = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show interface firewall", mode, bqnId))
    .then(parseFirewallList)
    .then(enumerate(1, { stored: true }));

const enumerate =
  (start = 1, extraParams) =>
  (list) =>
    list.map((item, index) => ({
      ...item,
      __id: start + index,
      ...extraParams,
    }));

const getManagementFirewallSettings = (mode, bqnId) =>
  getFirewallList(mode, bqnId).then((ranges) => ({ ranges }));

class RequiresConfirmation extends Error {
  constructor(reason) {
    super();
    this.reason = reason;
  }
}
class UserDeclined extends Error {}

const confirmWarning = (message, content) => {
  const done = new Promise((resolve) =>
    $("#modalConfirm").on("hidden.bs.modal", resolve)
  );
  return new Promise((resolve, reject) =>
    showModalConfirm(
      message,
      content,
      "warning",
      () => done.then(() => resolve(true)),
      () => done.then(() => reject(new UserDeclined(content))),
      "OK",
      "CANCEL"
    )
  );
};

const ManagementFirewallContextMenu = ({ doLoad = doesNothing }) => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
        <a
          onClick={doLoad}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
      <li className="dropdown hidden-to-operators">
        <a
          className="dropdown-toggle"
          data-toggle="dropdown"
          role="button"
          aria-haspopup="true"
          aria-expanded="false"
          title="Options"
        >
          <i className="material-icons">more_vert</i>
        </a>
        <ul className="dropdown-menu pull-right">
          <li>
            <a onClick={() => actions.send("new-range")}>
              Add IP Address Range...
            </a>
          </li>
          <li>
            <a onClick={() => actions.send("load-suggestions")}>
              Load Suggestions...
            </a>
          </li>
        </ul>
      </li>
    </ul>
  );
};

const mayRequireConfirmation = (warning) => {
  console.error("mayRequireConfirmation", warning);
  if (warning instanceof RequiresConfirmation) {
    return confirmWarning("Do you want to proceed?", warning.reason);
  }
  throw warning;
};

const checkForCollidingChanges = (previous, settings) => {
  const collisions = areChangesColliding(previous, settings);
  if (collisions.length > 0) {
    const reason =
      `The specified IP address ranges will permanently ` +
      `<a class="col-red">block</a> traffic from one or more needed sources. ` +
      `To avoid that press <code>cancel</code> button and select ` +
      `<code>Reload suggestions</code> option.`;
    throw new RequiresConfirmation(reason);
  }
  return true;
};

const getInterfaceManagementDetail = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show interface management detail", mode, bqnId))
    .then(response =>
      parseClusterResponse(response, parseManagementInterface, bqnId))

const doChecksOnBeforeProceed = (originalSettings, settings) => (commands) =>
  Promise.resolve(commands)
    .then(() => checkForCollidingChanges(originalSettings, settings))
    .catch(mayRequireConfirmation)
    .then(() => commands);

const ManagementFirewallConfig = () => {
  const [managementInterfaces, setManagementInterfaces] = useState({});
  const actions = useContext(ActionsContext);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  const load = () => {
    return Promise.all([
      getManagementFirewallSettings(mode, bqnId),
      getInterfaceManagementDetail(mode, bqnId),
    ]).then(([firewallResp, detailResp]) => {      
      const bqnMap = {};
      const interfaces = {};
      for(const [bqn, detail] of Object.entries(detailResp)) {
        bqnMap[detail.interface] = bqn;
        interfaces[bqn] = detail.interface;
      }

      setManagementInterfaces(interfaces);
      
      for(const range of firewallResp.ranges){
        range.bqn = bqnMap[range.interface];
      }

      return firewallResp
    })
  }

  const checkNothingPending = () =>
    ifCl.run("show config diff").then((response) => {
      if (response.length > 0) {
        throw new Error(CANT_PROCEED_ERROR);
      }
    });

  const clearConfigAttempt = () =>
    ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

  const notifyAndRevert = (error) => {
    if (error instanceof UserDeclined) {
      return { error: 'User declined' };
    }
    error = error.message === undefined ? new Error(error) : error;
    clearConfigAttempt();
    throw error;
  };

  const submitCommand = (command) =>
    ifCl.run(command + "\n", /*is batch*/ true);

  const apply = (settings, originalSettings = {}) =>
    checkNothingPending()
      .then(doChecksOnBeforeProceed(originalSettings, settings))
      .then(() => composeApplyCommand(settings))
      .then(cmds => Promise.all(cmds.map(cmd => submitCommand(cmd))))
      .then(() => actions.send("do-load"))
      .catch(notifyAndRevert);

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry their option, and their answers would be
  // those of the whole cluster.
  return bqnId === undefined ? (
    <Loading />
  ) : (
    <SettingsView
      key={`firewall-config-bqn${bqnId}`}
      className="container-lg"
      load={load}
      apply={apply}
      clear={clear}
      ContextMenu={ManagementFirewallContextMenu}
      title="Management interface firewall"
      applyActionClassName="hidden-to-operators"
      wrapIntoForm={false}
      subtitle={
        <>
          Press <code>Apply</code> button at the end of the page to set the new
          configuration values.
        </>
      }
      mdHelp={help}
    >
      <ShowManagementFirewallSettings managementInterfaces={managementInterfaces}/>
    </SettingsView>
  );
};

const ShowManagementFirewallSettings = ({
  defaults,
  onStateUpdate = doesNothing,
  managementInterfaces,
}) => {
  const { isOperator } = useProfile() || {};
  const actions = useContext(ActionsContext);

  const updateRanges = (ranges) => {
    onStateUpdate({ ranges });
  };

  return (
    <div className="body">
      <div className="row margin-b-10">
        <button
          type="button"
          className="btn btn-default waves-effect shadow-none"
          onClick={() => actions.send("new-range")}
        >
          <i className="material-icons">cloud_queue</i>
          <span>Add IP Address Range...</span>
        </button>
        <button
          type="button"
          className="btn btn-default waves-effect shadow-none"
          onClick={() => actions.send("load-suggestions")}
        >
          <i className="material-icons">list</i>
          <span>Load Suggestions...</span>
        </button>
      </div>
      <div className="row">
        <Ranges
          isOperator={isOperator}
          initial={defaults.ranges}
          onChange={updateRanges}
          managementInterfaces={managementInterfaces}
        />
      </div>
    </div>
  );
};

export default ManagementFirewallConfig;
